import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { useCopyBlobsToCompanionsMutation } from '@/services/api/blobService';
import type { CopyBlobsToCompanionsResponse } from '@/services/types/blob-types';

import { CompanionCopyResults } from './CompanionCopyResults';

interface TransferToCompanionsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  selectedBlobIds: string[];
  onSuccess?: () => void;
}

export const TransferToCompanionsDialog = ({
  isOpen,
  onClose,
  matterId,
  selectedBlobIds,
  onSuccess,
}: TransferToCompanionsDialogProps) => {
  const [copyToCompanions, { isLoading }] = useCopyBlobsToCompanionsMutation();
  const [previewResult, setPreviewResult] =
    useState<CopyBlobsToCompanionsResponse | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load dry-run preview when dialog opens
  useEffect(() => {
    if (isOpen && selectedBlobIds.length > 0) {
      loadPreview();
    } else {
      setPreviewResult(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedBlobIds.length]);

  const loadPreview = async () => {
    try {
      setError(null);
      const result = await copyToCompanions({
        matterId,
        body: {
          blob_ids: selectedBlobIds,
          dry_run: true,
        },
      }).unwrap();
      setPreviewResult(result);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setError('Failed to load preview. Please try again.');
    }
  };

  const handleExecute = async () => {
    try {
      setIsExecuting(true);
      setError(null);
      await copyToCompanions({
        matterId,
        body: {
          blob_ids: selectedBlobIds,
          dry_run: false,
        },
      }).unwrap();

      toast.success(
        `Successfully transferred ${selectedBlobIds.length} file(s) to companion matters`,
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to transfer files:', err);
      setError('Failed to transfer files. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleClose = () => {
    if (!isExecuting && !isLoading) {
      onClose();
    }
  };

  const totalToCreate = previewResult?.companions.reduce(
    (sum, companion) =>
      sum + companion.actions.filter((a) => a.action === 'create').length,
    0,
  );

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>Transfer to Companion Matters</DialogTitle>
      <DialogDescription>
        Preview what will be copied to companion matters for the selected{' '}
        {selectedBlobIds.length} file(s).
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="text-sm text-zinc-600">Loading preview...</div>
          </div>
        )}

        {!isLoading && previewResult && (
          <CompanionCopyResults result={previewResult} />
        )}
      </DialogBody>
      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isExecuting || isLoading}
          className="border border-zinc-300"
        >
          Cancel
        </Button>
        <Button
          onClick={handleExecute}
          disabled={
            isExecuting || isLoading || !previewResult || totalToCreate === 0
          }
        >
          {isExecuting ? 'Transferring...' : 'Execute Transfer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
